/**
 * requirementsParser.js
 *
 * Parses a requirements.md file into structured data for the DLC panel.
 *
 * Supports two formats auto-detected at parse time:
 *
 * ── Format A (structured list) ──────────────────────────────────────────────
 *
 *   ## Section Name <!-- SRS-SEC-003 -->
 *
 *   ### [MCREQUI-405] Requirement Title
 *   - **Priority:** High
 *   - **Type:** SRS
 *   - **Status:** Draft
 *
 * ── Format B (document-style, one ## per requirement) ──────────────────────
 *
 *   ## Requirement 1: Title
 *
 *   **ID**: REQ-001
 *   **Priority**: High
 *   **Status**: To Do
 *
 *   ### Description
 *   Implementation text...
 *
 *   ### Acceptance Criteria
 *   - bullet...
 *
 *   ### Dependencies
 *   - bullet...
 *
 *   ---
 *
 * Fields are case-insensitive. Format B wraps all requirements in a single
 * implicit section.
 */

const FIELD_ALIASES = {
  id: 'id',
  priority: 'priority',
  type: 'type',
  category: 'category',
  assignee: 'assignee',
  status: 'status',
  version: 'version',
  pr: 'pr',
  description: 'description',
  date: 'date',
};

// ─── Auto-detect format ───────────────────────────────────────────────────

/**
 * Heuristic: Format B has `**ID**:` or `**Priority**:` directly under a `##`
 * heading (not under a `###`).
 */
function detectFormatB(lines) {
  let inH2 = false;
  for (const line of lines) {
    const t = line.trim();
    if (t.startsWith('## ')) { inH2 = true; continue; }
    if (t.startsWith('### ') && inH2) { inH2 = false; continue; }
    if (inH2 && t.match(/^\*\*ID\*\*\s*:/i)) return true;
    if (inH2 && t.match(/^\*\*Priority\*\*\s*:/i)) return true;
    if (inH2 && t.match(/^\*\*Status\*\*\s*:/i)) return true;
  }
  return false;
}

// ─── Format B parser ─────────────────────────────────────────────────────

function parseFormatB(lines) {
  const requirements = [];
  let currentReq = null;
  let currentSubSection = null; // 'description' | 'acceptance' | 'dependencies' | null
  let descLines = [];

  const flushSub = () => {
    if (!currentReq || !currentSubSection) return;
    const text = descLines.join('\n').trim();
    if (currentSubSection === 'description' && text) {
      currentReq.description = text;
    } else if (currentSubSection === 'acceptance' && text) {
      currentReq.acceptanceCriteria = text;
    } else if (currentSubSection === 'dependencies' && text) {
      currentReq.dependencies = text;
    }
    descLines = [];
    currentSubSection = null;
  };

  const flushReq = () => {
    flushSub();
    if (currentReq) {
      requirements.push(currentReq);
      currentReq = null;
    }
  };

  for (const line of lines) {
    const t = line.trim();

    // Horizontal rule = requirement separator (flush current)
    if (t === '---' || t === '***' || t === '___') {
      flushReq();
      continue;
    }

    // Level-2 heading = new requirement
    const h2 = t.match(/^##\s+(.+)$/);
    if (h2) {
      flushReq();
      const rawTitle = h2[1].trim();
      // Strip leading "Requirement N: " prefix for the title field
      const titleMatch = rawTitle.match(/^Requirement\s+\d+[:.]?\s+(.+)$/i);
      currentReq = {
        id: '',
        title: titleMatch ? titleMatch[1].trim() : rawTitle,
        fullTitle: rawTitle,
        priority: '',
        type: '',
        category: '',
        assignee: '',
        status: '',
        version: '',
        pr: '',
        description: '',
        acceptanceCriteria: '',
        dependencies: '',
        date: '',
      };
      currentSubSection = null;
      descLines = [];
      continue;
    }

    if (!currentReq) continue;

    // Level-3 heading = sub-section label (Description / Acceptance Criteria / Dependencies)
    const h3 = t.match(/^###\s+(.+)$/);
    if (h3) {
      flushSub();
      const label = h3[1].trim().toLowerCase();
      if (label.includes('description')) currentSubSection = 'description';
      else if (label.includes('acceptance')) currentSubSection = 'acceptance';
      else if (label.includes('depend')) currentSubSection = 'dependencies';
      else currentSubSection = null;
      continue;
    }

    // Inline bold key-value metadata: **Key**: Value  (Format B metadata lines)
    const kvMatch = t.match(/^\*\*([^*]+)\*\*\s*:?\s+(.+)$/);
    if (kvMatch && !currentSubSection) {
      const key = kvMatch[1].trim().toLowerCase();
      const value = kvMatch[2].trim();
      const alias = FIELD_ALIASES[key];
      if (alias) {
        currentReq[alias] = value;
      }
      continue;
    }

    // Collect sub-section body lines
    if (currentSubSection) {
      descLines.push(line.replace(/\r$/, ''));
    }
  }

  flushReq();

  return {
    sections: [{
      id: '',
      title: 'Requirements',
      requirements,
    }],
  };
}

// ─── Format A parser (original) ──────────────────────────────────────────

function parseFormatA(lines) {
  const sections = [];
  let currentSection = null;
  let currentReq = null;
  let currentReqLines = [];

  const flushRequirement = () => {
    if (currentReq && currentSection) {
      const fields = parseRequirementFields(currentReqLines);
      Object.assign(currentReq, fields);
      currentSection.requirements.push(currentReq);
      currentReq = null;
      currentReqLines = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();

    // Level-2 heading = new section
    const sectionMatch = trimmed.match(/^##\s+(.+?)(?:\s*<!--\s*([A-Z0-9-]+)\s*-->)?$/);
    if (sectionMatch) {
      flushRequirement();
      if (currentSection) sections.push(currentSection);
      const sectionTitle = sectionMatch[1].trim();
      const sectionId = sectionMatch[2] || '';
      currentSection = { id: sectionId, title: sectionTitle, requirements: [] };
      continue;
    }

    // Bare section ID line
    if (currentSection && currentSection.requirements.length === 0 && !currentReq) {
      const bareId = trimmed.match(/^([A-Z]{2,}-[A-Z]{2,}-\d{3})$/);
      if (bareId && !currentSection.id) {
        currentSection.id = bareId[1];
        continue;
      }
    }

    // Level-3 heading = new requirement
    const reqMatch = trimmed.match(/^###\s+(?:\[([A-Z]+-\d+)\]\s+)?(.+)$/);
    if (reqMatch) {
      flushRequirement();
      if (!currentSection) {
        currentSection = { id: '', title: 'General', requirements: [] };
      }
      const reqId = reqMatch[1] || '';
      const reqTitle = reqMatch[2].trim();
      currentReq = {
        id: reqId,
        title: reqTitle,
        priority: '',
        type: '',
        category: '',
        assignee: '',
        status: '',
        version: '',
        pr: '',
        description: '',
        date: '',
      };
      currentReqLines = [];
      continue;
    }

    // Collect field lines
    if (currentReq) {
      if (trimmed.startsWith('-') || trimmed.startsWith('*') || trimmed.match(/^\*\*\w+/)) {
        currentReqLines.push(trimmed);
      } else if (trimmed.length > 0 && !trimmed.startsWith('#')) {
        currentReqLines.push(trimmed);
      }
    }
  }

  flushRequirement();
  if (currentSection) sections.push(currentSection);

  return { sections };
}

/**
 * Parse requirements.md text into sections and requirements.
 *
 * @param {string} markdown - Raw markdown content of requirements.md
 * @returns {{ sections: Section[] }}
 */
export function parseRequirementsMarkdown(markdown) {
  if (!markdown || typeof markdown !== 'string') {
    return { sections: [] };
  }

  const lines = markdown.split('\n');
  return detectFormatB(lines) ? parseFormatB(lines) : parseFormatA(lines);
}


/**
 * Parse bullet-list field lines from a requirement block (Format A).
 * e.g. "- **Priority:** High" or "**Priority:** High"
 */
function parseRequirementFields(lines) {
  const fields = {};
  let descLines = [];

  for (const line of lines) {
    // Match "- **Key:** Value" or "**Key:** Value"
    const fieldMatch = line.match(/^[-*]?\s*\*\*([^*]+)\*\*:?\s*(.*)$/i);
    if (fieldMatch) {
      const key = fieldMatch[1].trim().toLowerCase();
      const value = fieldMatch[2].trim().replace(/^<!--.*-->$/, '').trim();
      const alias = FIELD_ALIASES[key];
      if (alias) {
        fields[alias] = value;
      }
    } else if (line.trim().length > 0) {
      descLines.push(line.replace(/^[-*]\s*/, '').trim());
    }
  }

  if (descLines.length > 0 && !fields.description) {
    fields.description = descLines.join(' ');
  }

  return fields;
}

/**
 * Flatten all requirements across sections into a single array.
 * Each requirement carries a `sectionId` and `sectionTitle` property.
 *
 * @param {{ sections: Section[] }} parsed
 * @returns {Requirement[]}
 */
export function flattenRequirements(parsed) {
  const all = [];
  for (const section of parsed.sections) {
    for (const req of section.requirements) {
      all.push({ ...req, sectionId: section.id, sectionTitle: section.title });
    }
  }
  return all;
}

/**
 * Merge BFF DLC state (from dlcService) into a parsed requirement.
 * BFF state takes precedence for: status, pr, agentConfig, lastRunAt.
 *
 * @param {Requirement} req
 * @param {Object} dlcState - DLC state object from BFF
 * @returns {Requirement}
 */
export function mergeWithDlcState(req, dlcState) {
  if (!dlcState) return req;
  return {
    ...req,
    status: dlcState.status || req.status,
    pr: dlcState.linkedPR ? `#${dlcState.linkedPR.number}` : req.pr,
    linkedPR: dlcState.linkedPR || null,
    agentConfig: dlcState.agentConfig || null,
    lastRunAt: dlcState.lastRunAt || null,
    swiId: dlcState.swiId || null,
  };
}
